import React from "react";

import { CESION_ECHEQ_ACTIVITY, FormattedAmount, NEGOTIATE_ECHEQ_ACTIVITY } from "@ui-kit/components/index.es";

import { echeqConceptLabel } from "util/checks";
import * as configUtils from "util/config";
import formatLegacyWebDateTimeString from "util/formatLegacyWebDateTimeString";
import * as i18n from "util/i18n";
import { isFinalState } from "util/transaction";

/** Legacy escapes `Ñ` as `#` in beneficiary and reference free text. */
function unescapeSpanishN(value) {
    return typeof value === "string" ? value.replaceAll("#", "Ñ") : value;
}

/**
 * COELSA rejection reason and code, only while the failed-voucher flag is off (legacy behaviour).
 * Negotiation restricts them to `FAILED` and prefers the IBS response.
 * @param {object} transactionData
 * @param {boolean} isNegotiate
 * @returns {{ coelsaResponse: string|null, coelsaCode: string|null }}
 */
function resolveCoelsaFields(transactionData, isNegotiate) {
    const hideCoelsaFields = configUtils.getBoolean("frontend.show.failedVoucher.chequesFunctionalities", false);
    if (hideCoelsaFields) {
        return { coelsaResponse: null, coelsaCode: null };
    }
    if (isNegotiate) {
        if (transactionData.idTransactionStatus !== "FAILED") {
            return { coelsaResponse: null, coelsaCode: null };
        }
        return {
            coelsaResponse: transactionData.ibsResponse || transactionData.coelsaResponse || null,
            coelsaCode: null,
        };
    }
    return {
        coelsaResponse: transactionData.coelsaResponse || null,
        coelsaCode: transactionData.coelsaCode || null,
    };
}

/**
 * Failed-voucher code and message, only on `FAILED` receipts. Mirrors the legacy
 * `TransactionTicket` fallbacks: the code and the message may travel either at the summary root or
 * inside `data`, and the message falls back to the voucher service message.
 *
 * @param {object} transactionData
 * @returns {{ voucherErrorCode: string|null, voucherErrorMessage: string|null }}
 */
function resolveFailedVoucherFields(transactionData) {
    if (transactionData.idTransactionStatus !== "FAILED") {
        return { voucherErrorCode: null, voucherErrorMessage: null };
    }
    return {
        voucherErrorCode: transactionData.voucherErrorCode || transactionData.data?.voucherErrorCode || null,
        voucherErrorMessage:
            transactionData.voucherErrorMessage ||
            transactionData.data?.voucherErrorMessage ||
            transactionData.voucherServiceMessage ||
            transactionData.data?.voucherServiceMessage ||
            null,
    };
}

/** `cancelledTime` is stored as `dd/MM/yyyy HH:mm:ss` and legacy prints it as-is. */
const LEGACY_CANCELLED_TIME_PATTERN = /^\d{2}\/\d{2}\/\d{4}/;

/**
 * "Cancelado por" entry of a `CANCELLED` receipt, with the same precedence as the legacy
 * `TransactionTicket`: the expiry daemon leaves a `returnCode` (and no user), while a manual
 * cancellation persists `cancelledUser` / `cancelledTime`.
 *
 * @param {object|null} transactionData
 * @returns {{ userFullName: string, creationDateTime: string|undefined }|undefined}
 */
export function buildEcheqEndorseCancelledBy(transactionData) {
    if (transactionData?.idTransactionStatus !== "CANCELLED") {
        return undefined;
    }

    const userFullName = transactionData.returnCode
        ? i18n.get(`returnCode.${transactionData.returnCode}`)
        : transactionData.cancelledUser || transactionData.data?.cancelledUser || "";

    if (!userFullName) {
        return undefined;
    }

    return {
        userFullName,
        creationDateTime: transactionData.returnCode
            ? transactionData.submitDateTimeAsString
            : transactionData.cancelledTime || transactionData.data?.cancelledTime || undefined,
    };
}

/**
 * Formats the signature/cancellation moment keeping the legacy `dd/MM/yyyy HH:mm:ss` strings
 * (`cancelledTime`, `submitDateTimeAsString`) untouched.
 *
 * @param {string|number|Date|null|undefined} input
 * @returns {string}
 */
export function formatEcheqEndorseSignatureDate(input) {
    if (typeof input === "string" && LEGACY_CANCELLED_TIME_PATTERN.test(input.trim())) {
        return input.trim();
    }
    return formatLegacyWebDateTimeString(input);
}

/**
 * Builds the display-ready `EcheqEndorseDataInput` consumed by the UI-kit `EcheqEndorseTicket`
 * from the Redux `checks.transactionEcheqData` summary. Mirrors the fields and conditions of the
 * legacy `EcheqTransactionData` / `NegotiateEcheqTransactionData` / `CesionEcheqTransactionData`
 * components.
 *
 * @param {object|null} transactionData
 * @returns {object|null} DTO for the UI-kit page, or `null` when there is no transaction yet
 */
export default function buildEcheqEndorseDataInput(transactionData) {
    const echeq = transactionData?.echeq;
    if (!transactionData?.idTransaction || !echeq) {
        return null;
    }

    const isNegotiate = transactionData.idActivity === NEGOTIATE_ECHEQ_ACTIVITY;
    const isCesion = transactionData.idActivity === CESION_ECHEQ_ACTIVITY;
    const { coelsaResponse, coelsaCode } = resolveCoelsaFields(transactionData, isNegotiate);
    const { voucherErrorCode, voucherErrorMessage } = resolveFailedVoucherFields(transactionData);
    const beneficiaryReference = transactionData.beneficiary?.reference;

    return {
        idActivity: transactionData.idActivity,
        idTransaction: transactionData.idTransaction,
        idTransactionStatus: transactionData.idTransactionStatus,
        // Legacy only prints the operation date on final states; pending receipts show no date row.
        operationDate: isFinalState(transactionData.idTransactionStatus) ? transactionData.submitDateTime : null,
        endorsementDateLabel: isNegotiate
            ? null
            : formatLegacyWebDateTimeString(transactionData.submitDateTime) || null,
        endorsementTypeLabel: transactionData.type
            ? i18n.get(`echeqs.${transactionData.type.toLowerCase()}.label`)
            : null,
        cesionDateLabel: isCesion ? transactionData.cesion?.fecha_emision_cesion : null,
        cesionId: isCesion ? transactionData.cesion?.cesion_id : null,
        beneficiaryName: isNegotiate ? beneficiaryReference : unescapeSpanishN(beneficiaryReference),
        beneficiaryDocument: isNegotiate ? null : transactionData.beneficiary?.document,
        addressLabel: isCesion ? transactionData.address : null,
        accountLabel: isNegotiate ? transactionData.accountLabel : null,
        reference: isNegotiate ? unescapeSpanishN(transactionData.reference) : transactionData.reference,
        referenceValue: transactionData.referencevalue,
        echeqNumber: echeq.cheque_numero,
        echeqId: echeq.cheque_id,
        issueDateLabel: formatLegacyWebDateTimeString(echeq.fecha_emision, false) || null,
        paymentDateLabel: formatLegacyWebDateTimeString(echeq.fecha_pago, false) || null,
        // `variant="dd"` keeps the amount aligned with the other ticket values (Graphik-Medium 16px).
        amountNode: (
            <FormattedAmount
                amount={echeq.monto}
                currency={i18n.get(`currency.label.${echeq.moneda || "0"}`, echeq.moneda)}
                variant="dd"
            />
        ),
        echeqTypeLabel: echeq.cheque_tipo ? i18n.get(`echeqs.emit.${echeq.cheque_tipo.toLowerCase()}.label`) : null,
        echeqCharacter: echeq.cheque_caracter,
        echeqModeLabel: echeq.cheque_modo ? i18n.get(`echeqs.${echeq.cheque_modo}.modo`) : null,
        conceptLabel: `${echeq.cheque_concepto} ${echeqConceptLabel(echeq.cheque_concepto, "-")}`,
        paymentReason: echeq.cheque_motivo_pago,
        coelsaResponse,
        coelsaCode,
        voucherErrorCode,
        voucherErrorMessage,
    };
}
